/**
 * Analytics Utilities
 *
 * Helper functions for tracking custom events with Mixpanel
 */

declare global {
  interface Window {
    mixpanel: any
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>,
) {
  if (typeof window !== 'undefined' && window.mixpanel) {
    window.mixpanel.track(eventName, properties)
    console.log('[Analytics] Event tracked:', eventName, properties)
  }
}

/**
 * Track page view
 */
export function trackPageView(
  pageName: string,
  properties?: Record<string, any>,
) {
  trackEvent('Page View', {
    page: pageName,
    ...properties,
  })
}

/**
 * Track cryptocurrency view
 */
export function trackCryptoView(
  cryptoName: string,
  symbol: string,
  price: number,
) {
  trackEvent('Crypto Viewed', {
    crypto_name: cryptoName,
    symbol: symbol,
    price: price,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track exchange view
 */
export function trackExchangeView(exchangeName: string, rank: number) {
  trackEvent('Exchange Viewed', {
    exchange_name: exchangeName,
    rank: rank,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track comparison action
 */
export function trackComparison(
  itemType: 'crypto' | 'exchange',
  itemNames: string[],
) {
  trackEvent('Comparison Started', {
    type: itemType,
    items: itemNames,
    count: itemNames.length,
  })
}

/**
 * Track share action
 */
export function trackShare(
  platform: string,
  contentType: string,
  contentName: string,
) {
  trackEvent('Content Shared', {
    platform: platform,
    content_type: contentType,
    content_name: contentName,
  })
}

/**
 * Track price refresh
 */
export function trackPriceRefresh(source: string) {
  trackEvent('Price Refreshed', {
    source: source,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track search
 */
export function trackSearch(query: string, resultsCount: number) {
  trackEvent('Search Performed', {
    query: query,
    results_count: resultsCount,
  })
}

/**
 * Identify user (optional - for logged in users)
 */
export function identifyUser(userId: string, properties?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.mixpanel) {
    window.mixpanel.identify(userId)
    if (properties) {
      window.mixpanel.people.set(properties)
    }
  }
}
